import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';

import { Button } from '@/components/Button/Button';
import { OwnClientsBadge } from '@/features/auth/OwnClientsBadge';
import { Permission } from '@/features/auth/permissions';
import { sessionQuery } from '@/features/auth/session';
import { DashboardOrdersCard } from '@/features/dashboard/DashboardOrdersCard';
import { NotificationsCard } from '@/features/notifications/NotificationsCard';
import { COPY as ORDER_COPY } from '@/features/orders/copy';
import { SalesCard } from '@/features/reports/dashboard/SalesCard';
import { GlobalSearch } from '@/features/search/GlobalSearch';
import { XeroAlert } from '@/features/xero/XeroAlert';
import { XeroBadge } from '@/features/xero/XeroBadge';
import { Page, PageBody } from '@/layouts/Page';
import { PageHeader } from '@/layouts/PageHeader';
import { cn } from '@/util/utils';

export const Route = createFileRoute('/_app/dashboard')({
    component: DashboardPage,
});

/*
  The header as the prototype draws it (screens/dashboard.html, ALS-37): the greeting over the
  title, the global search, the Xero badge and New order. The Xero alert sits above the body
  (frames 1.1b to 1.1d, ALS-68), and blocks none of it. Under it the top row: the notifications
  card (ALS-71), beside the Sales card (ALS-85). Below that the orders preview (ALS-55), shown
  to whoever may see orders.
*/
function DashboardPage() {
    const { data: user } = useQuery(sessionQuery);
    const canOrder = user?.permissions.includes(Permission.EditOrders) ?? false;
    const canReport = user?.permissions.includes(Permission.ViewReports) ?? false;
    const canSeeOrders = user?.permissions.includes(Permission.ViewOrders) ?? false;
    return (
        <Page surface="main">
            <PageHeader
                eyebrow={greeting()}
                title="Dashboard"
                search={<GlobalSearch />}
                actions={
                    <>
                        <OwnClientsBadge />
                        <XeroBadge />
                        {canOrder ? (
                            // Default height, as dashboard.html draws it (no size on its ui-button).
                            <Button asChild>
                                <Link to="/orders/new">
                                    <Plus />
                                    {ORDER_COPY.add}
                                </Link>
                            </Button>
                        ) : null}
                    </>
                }
            />
            <PageBody>
                <XeroAlert />
                {/* The top row as the prototype draws it: Sales on the left, Notifications beside it.
                    Sales is the report's figures (ALS-85), so a role without reports sees
                    Notifications alone, across the row. */}
                <div
                    className={cn(
                        'grid min-w-0 gap-(--layout-row-gap)',
                        canReport && 'lg:grid-cols-[minmax(0,1fr)_minmax(0,470px)]',
                    )}
                >
                    {canReport ? <SalesCard /> : null}
                    <NotificationsCard />
                </div>
                {canSeeOrders ? <DashboardOrdersCard /> : null}
            </PageBody>
        </Page>
    );
}

function greeting() {
    const hour = new Date().getHours();
    return hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
}
